import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgModule, OnInit } from '@angular/core';
import { Cms, PAGE_TYPES } from '@impact/data';
import { MapsLocation, MapView } from '../../maps/maps.component';
import { MapsModule } from '../../maps/maps.module';
import { NavigationService } from '../../navigation/navigation.service';
import { NewCarsModule } from '../../new-cars/new-cars.module';
import { SpotsUiModule } from '../../spots/spots-ui.module';
import { carouselSpotData } from '../../core/helpers';
import { take } from 'rxjs/operators';
import { GlobalDataService } from '../../core/global-data.service';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-vehicle-make-page',
    template: `
        <main *ngIf="data">
            <div class="spot-spacing" *ngIf="data?.showTopBanner && topBannerData?.items">
                <app-carousel-spot
                    [ngClass]="{'small-banner': data?.displaySmallBanner}"
                    [data]="topBannerData"
                    [lazyLoad]="true">
                </app-carousel-spot>
            </div>

            <section class="spot-spacing" *ngIf="data?.makeFromNavigationNode">
                <app-new-cars-card-list
                    *ngIf="data.vehicleMake.id"
                    [excludeModel]="data.makeFromNavigationNode.excludeModel"
                    [layoutMode]="data.makeFromNavigationNode.layoutMode"
                    [vehiclePageId]="data.id"
                ></app-new-cars-card-list>
            </section>

            <section class="spot-spacing">
                <app-maps *ngIf="dealershipLocations.length"
                    [vehicleMakeName]="vehicleMakeName"
                    [locations]="dealershipLocations"
                    [mapView]="mapView.DEALERSHIPS_WITH_MAKE">
                </app-maps>
            </section>

            <section class="page-grid-content">
                <app-spot-grid [grid]="data?.mainGrid"></app-spot-grid>
            </section>
        </main>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
class NewVehicleMakePageComponent implements OnInit {
    public static ref = PAGE_TYPES.VEHICLE_MAKE_PAGE;
    @Input() data: Cms.NewVehiclesMakePage;

    topBannerData: Cms.CarouselSpot;
    mapView = MapView;
    dealershipsData: MapsLocation[] = [];
    dealershipLocations: MapsLocation[] = [];
    vehicleMakeName: string;

    constructor(
        private globalDataService: GlobalDataService,
        private navigationService: NavigationService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        if (this.data.vehicleMake?.vehicleMakeTag?.name) {
            this.vehicleMakeName = this.data.vehicleMake.vehicleMakeTag.name;
            this.mapDealershipsData();
        }

        // Map dealership page carousel data to correct interface
        if (this.data.carouselContentTop) {
            this.topBannerData = carouselSpotData(this.data.carouselContentTop);
        } else {
            this.data.showTopBanner = false;
        }

        this.navigationService.setActiveBrand(this.data?.vehicleMake?.name);
    }

    mapDealershipsData(): void {
        forkJoin([
            this.globalDataService.getDealerships(),
            this.navigationService.dealershipsNavigation$.pipe(take(1))
        ]).subscribe(([dealerships, dealershipsNavigation]) => {
            dealershipsNavigation.forEach((navItem) => {
                if (navItem.dealerShipId) {
                    const matchingDealership = dealerships.children.find(dealership => dealership.id === navItem.dealerShipId);
                    if (matchingDealership) {
                        this.dealershipsData.push({
                            ...matchingDealership,
                            pageUrl: navItem.url
                        });
                    }
                }
            });

            if (this.dealershipsData.length) {
                this.findMakeDealerships(this.dealershipsData);
            } else {
                console.warn(`[BN error: No ${this.vehicleMakeName} dealerships found]`);
            }

        });
    }

    findMakeDealerships(dealershipsData: MapsLocation[]): void {
        dealershipsData.forEach((dealership) => {
            if (dealership.vehicleMakes) {
                if (dealership.vehicleMakes.some((make) => make.name.toLowerCase() === this.vehicleMakeName.toLowerCase())) {
                    this.dealershipLocations.push(dealership);
                }
            }
        });

        if (!this.dealershipLocations.length) {
            console.warn(`[BN error: No ${this.vehicleMakeName} dealerships found]`);
        }

        this.cdr.markForCheck();
    }
}

@NgModule({
    declarations: [NewVehicleMakePageComponent],
    imports: [
        NewCarsModule,
        CommonModule,
        SpotsUiModule,
        MapsModule
    ],
})
class VehicleMakePageModule {}

export const LazyComponent = NewVehicleMakePageComponent;
